import React from 'react';

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 32,
      paddingBottom: 20,
      borderBottom: '1px solid var(--border)',
      gap: 24,
    }}>
      <div>
        {eyebrow && (
          <div style={{
            fontSize: 9, fontWeight: 200, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: 'var(--earth)',
            marginBottom: 8,
          }}>{eyebrow}</div>
        )}
        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 32, fontWeight: 300, lineHeight: 1.1, marginBottom: 4,
        }}>{title}</h1>
        {subtitle && (
          <p style={{ fontSize: 13, fontWeight: 300, color: 'var(--ink-soft)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}

export function Button({ children, variant = 'primary', size = 'md', ...rest }) {
  const variants = {
    primary: {
      background: 'var(--ink)', color: 'var(--sand)',
      border: '1px solid var(--ink)',
    },
    secondary: {
      background: 'transparent', color: 'var(--ink)',
      border: '1px solid var(--border-strong)',
    },
    ghost: {
      background: 'transparent', color: 'var(--ink-soft)',
      border: 'none',
    },
    danger: {
      background: 'var(--danger-bg)', color: 'var(--danger)',
      border: '1px solid #E0CFC4',
    },
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 11, letterSpacing: '0.1em' },
    md: { padding: '10px 18px', fontSize: 12, letterSpacing: '0.12em' },
  };
  return (
    <button
      {...rest}
      style={{
        ...variants[variant], ...sizes[size],
        borderRadius: 4,
        fontWeight: 300,
        textTransform: 'uppercase',
        cursor: rest.disabled ? 'default' : 'pointer',
        opacity: rest.disabled ? 0.5 : 1,
        transition: 'opacity 0.15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        ...rest.style,
      }}
    >
      {children}
    </button>
  );
}

export function Card({ children, padding = 20, ...rest }) {
  return (
    <div {...rest} style={{
      background: '#fff',
      border: '1px solid var(--border)',
      borderRadius: 6,
      padding,
      ...rest.style,
    }}>{children}</div>
  );
}

export function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: { bg: 'var(--sand-2)', fg: 'var(--ink-soft)' },
    success: { bg: 'var(--success-bg)', fg: 'var(--success)' },
    warning: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
    danger:  { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
  };
  const c = tones[tone];
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 12,
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.fg,
    }}>{children}</span>
  );
}

export function Input({ label, hint, ...rest }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <span style={{
          fontSize: 9, fontWeight: 300, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: 'var(--earth)',
        }}>{label}</span>
      )}
      <input {...rest} style={{
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        padding: '8px 0',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 300,
        color: 'var(--ink)',
        outline: 'none',
        ...rest.style,
      }}/>
      {hint && <span style={{ fontSize: 11, color: 'var(--stone)' }}>{hint}</span>}
    </label>
  );
}

export function Select({ label, children, ...rest }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <span style={{
          fontSize: 9, fontWeight: 300, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: 'var(--earth)',
        }}>{label}</span>
      )}
      <select {...rest} style={{
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        padding: '8px 0',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 300,
        color: 'var(--ink)',
        outline: 'none',
        borderRadius: 0,
        ...rest.style,
      }}>{children}</select>
    </label>
  );
}

export function Textarea({ label, ...rest }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <span style={{
          fontSize: 9, fontWeight: 300, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: 'var(--earth)',
        }}>{label}</span>
      )}
      <textarea {...rest} style={{
        background: 'transparent',
        border: '1px solid var(--border)',
        padding: 10,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 300,
        color: 'var(--ink)',
        outline: 'none',
        borderRadius: 4,
        resize: 'vertical',
        minHeight: 80,
        ...rest.style,
      }}/>
    </label>
  );
}

export function Modal({ open, onClose, title, children, maxWidth = 520 }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(44, 42, 38, 0.4)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--warm-white)',
          borderRadius: 8,
          width: '100%', maxWidth,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 28,
        }}
      >
        {title && (
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 22, fontWeight: 300,
            marginBottom: 4,
          }}>{title}</div>
        )}
        <div style={{ width: 24, height: 1, background: 'var(--earth)', marginBottom: 20 }} />
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ children }) {
  return (
    <div style={{
      padding: '48px 20px',
      textAlign: 'center',
      color: 'var(--stone)',
      fontSize: 13,
      fontWeight: 300,
    }}>{children}</div>
  );
}

export function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-AR', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
